import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { 
  Bitcoin,
  Code,
  Copy,
  Check,
  ExternalLink,
  LogOut,
  ArrowLeft,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import './Widget.css';

const Widget = () => {
  const { user, logout } = useContext(AuthContext);
  const [copied, setCopied] = useState(false);
  const [selectedMode, setSelectedMode] = useState('modal');
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const navigate = useNavigate();

  const WIDGET_URL = `${window.location.origin}/widget/crypto-cashier-widget.js`;
  const CHECKOUT_URL = `${window.location.origin}/checkout/${user?.id}`;

  const generateCode = () => {
    return `<!-- Add this before closing </body> tag -->
<script src="${WIDGET_URL}"></script>

<script>
  // Initialize widget
  const widget = new CryptoCashier({
    userId: '${user?.id}',
    mode: '${selectedMode}', // 'modal' or 'redirect'
    theme: '${selectedTheme}', // 'dark' or 'light'
    buttonText: 'Pay with Crypto',
    buttonStyle: {
      // Optional custom styles
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    onSuccess: function(data) {
      console.log('Payment successful!', data);
      // Your success handler
    },
    onCancel: function() {
      console.log('Checkout cancelled');
    }
  });

  // Create checkout button in your page
  widget.createButton('crypto-checkout-container');
  
  // Store widget globally for cart operations
  window.cryptoCashierWidget = widget;
</script>

<!-- Add this div where you want the button to appear -->
<div id="crypto-checkout-container"></div>`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (user?.role !== 'owner') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="widget-page">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          <div className="page-header">
            <Code size={32} />
            <h1>Website Integration</h1>
            <p>Add crypto checkout to your website</p>
          </div>

          {/* Integration Options */}
          <div className="integration-options">
            <div className="option-card">
              <h3>
                <Code size={20} />
                JavaScript Widget (Recommended)
              </h3>
              <p>Embed checkout directly on your website with a modal popup</p>
              <div className="features-list">
                <div className="feature-item">✅ Modal checkout (no redirect)</div>
                <div className="feature-item">✅ Customizable button & theme</div>
                <div className="feature-item">✅ Shopping cart included</div>
                <div className="feature-item">✅ Real-time payment updates</div>
              </div>
            </div>

            <div className="option-card">
              <h3>
                <Globe size={20} />
                Hosted Checkout Page
              </h3>
              <p>Share a direct link to your checkout page</p>
              <div className="checkout-link-box">
                <input 
                  type="text" 
                  value={CHECKOUT_URL} 
                  readOnly 
                  onClick={(e) => e.target.select()}
                />
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => copyToClipboard(CHECKOUT_URL)}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <a 
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  <ExternalLink size={16} /> Preview
                </a>
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="config-section">
            <h2>Configure Widget</h2>
            
            <div className="config-options">
              <div className="config-group">
                <label>Checkout Mode</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="mode" 
                      value="modal"
                      checked={selectedMode === 'modal'}
                      onChange={(e) => setSelectedMode(e.target.value)}
                    />
                    <span>Modal Popup</span>
                    <small>Opens in overlay (recommended)</small>
                  </label>
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="mode" 
                      value="redirect"
                      checked={selectedMode === 'redirect'}
                      onChange={(e) => setSelectedMode(e.target.value)}
                    />
                    <span>Redirect</span>
                    <small>Opens in new page</small>
                  </label>
                </div>
              </div>

              <div className="config-group">
                <label>Theme</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="theme" 
                      value="dark"
                      checked={selectedTheme === 'dark'}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                    />
                    <span>Dark</span>
                  </label>
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="theme" 
                      value="light"
                      checked={selectedTheme === 'light'}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                    />
                    <span>Light</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Code Snippet */}
          <div className="code-section">
            <div className="code-header">
              <h2>Installation Code</h2>
              <button 
                className="btn btn-primary"
                onClick={() => copyToClipboard(generateCode())}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
            </div>

            <div className="code-block">
              <pre><code>{generateCode()}</code></pre>
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions-section">
            <h2>How to Install</h2>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Copy the code above</h3>
                  <p>Use the "Copy Code" button to copy the installation snippet</p>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Add to your website</h3>
                  <p>Paste the code before the closing <code>&lt;/body&gt;</code> tag in your HTML</p>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Add the button container</h3>
                  <p>Place <code>&lt;div id="crypto-checkout-container"&gt;&lt;/div&gt;</code> where you want the checkout button</p>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Test your integration</h3>
                  <p>Visit your website and click the checkout button to test</p>
                </div>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="support-section">
            <h3>Need Help?</h3>
            <p>Contact our support team for integration assistance</p>
            <a href="mailto:support@crypto-cashier.com" className="btn btn-secondary">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Widget;
